import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loginUser, verificationUser, verifyLogin } from "../model/LoginModel";
import Logger from "../../base/Logger";
import UserUtils from "../UserUtils";
import AxiosWrapper from "../../base/AxiosWrapper";
import { useToast } from "../../components/provider/ToastProvider";

const { handleLoggedIn, getUserName } = UserUtils;
const { getBEUrl } = AxiosWrapper;

const LoginVM = () => {
  const { handleToastOpen } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessedCallback = useRef(false);

  // Handle OAuth callback and URL parameters
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Prevent duplicate processing
      if (hasProcessedCallback.current) {
        console.log("Already processed OAuth callback, skipping...");
        return;
      }

      const urlParams = new URLSearchParams(location.search);
      const msauthParam = urlParams.get("msauth");
      const emailParam = urlParams.get("email");
      const errorParam = urlParams.get("error");
      const expectedEmail = urlParams.get("expected");
      const gotEmail = urlParams.get("got");
      const loginParam = urlParams.get("login");

      console.log("=== URL Parameters ===");
      console.log("msauth:", msauthParam);
      console.log("email:", emailParam);
      console.log("error:", errorParam);
      console.log("login:", loginParam);

      // Handle error from Microsoft OAuth
      if (errorParam === "email_mismatch") {
        hasProcessedCallback.current = true;
        console.error("=== EMAIL MISMATCH ===");
        console.error("Expected:", expectedEmail);
        console.error("Got:", gotEmail);
        handleToastOpen("error", "Authentication failed. Please try again.");
        navigate("/login", { replace: true });
        return;
      }

      if (errorParam === "session_error") {
        hasProcessedCallback.current = true;
        console.error("=== SESSION ERROR ===");
        handleToastOpen("error", "Authentication failed. Please try again.");
        navigate("/login", { replace: true });
        return;
      }

      if (errorParam) {
        hasProcessedCallback.current = true;
        console.error("OAuth error:", errorParam);
        handleToastOpen("error", "Authentication failed. Please try again.");
        navigate("/login", { replace: true });
        return;
      }

      // Handle successful Microsoft authentication (NEW FLOW)
      if (msauthParam === "success" && emailParam) {
        hasProcessedCallback.current = true;
        console.log("=== Processing Microsoft Auth Success ===");
        console.log("Authenticated email:", emailParam);
        setLoading(true);

        try {
          console.log("Calling login API...");
          const result = await loginUser(emailParam);

          if (!result || !result.data) {
            throw new Error("Invalid response");
          }

          console.log("Login successful, storing credentials...");
          handleLoggedIn(result.data);

          // Dispatch storage event
          const event = new CustomEvent("cStorage", {});
          window.dispatchEvent(event);

          handleToastOpen("success", "Login successful!");

          // Handle redirect
          const logingOneUrl = sessionStorage.getItem("logingsoneurl");
          if (logingOneUrl) {
            console.log("Redirecting to stored URL:", logingOneUrl);
            sessionStorage.removeItem("logingsoneurl");
            setTimeout(() => {
              navigate(logingOneUrl.replace(window.location.origin, ""), { replace: true });
            }, 500);
          } else {
            console.log("Redirecting to /services");
            setTimeout(() => {
              navigate("/services", { replace: true });
            }, 500);
          }
        } catch (error) {
          console.error("Login error:", error);
          Logger.e("Login error:", error);
          handleToastOpen("error", "Authentication failed. Please try again.");
          setLoading(false);
          navigate("/login", { replace: true });
        }

        return;
      }

      // Handle old flow (login=true) for backward compatibility
      if (loginParam === "true") {
        hasProcessedCallback.current = true;
        console.log("=== Processing Old Login Flow (login=true) ===");
        setLoading(true);

        const storedEmail = sessionStorage.getItem("email");
        console.log("Stored email from sessionStorage:", storedEmail);

        if (!storedEmail) {
          console.error("No stored email found");
          handleToastOpen("error", "Authentication failed. Please try again.");
          navigate("/login", { replace: true });
          return;
        }

        const userName = getUserName();
        if (userName === null || userName === "") {
          try {
            console.log("Calling login API with stored email...");
            const result = await loginUser(storedEmail);
            
            handleLoggedIn(result.data);
            
            const event = new CustomEvent("cStorage", {});
            window.dispatchEvent(event);

            handleToastOpen("success", "Login successful!");

            // Handle redirect
            const logingOneUrl = sessionStorage.getItem("logingsoneurl");
            if (logingOneUrl) {
              sessionStorage.removeItem("logingsoneurl");
              setTimeout(() => {
                navigate(logingOneUrl.replace(window.location.origin, ""), { replace: true });
              }, 500);
            } else {
              setTimeout(() => {
                navigate("/services", { replace: true });
              }, 500);
            }
          } catch (error) {
            console.error("Login error:", error);
            Logger.e("Login error:", error);
            handleToastOpen("error", "Authentication failed. Please try again.");
            setLoading(false);
            navigate("/login", { replace: true });
          }
        } else {
          // User already logged in
          console.log("User already logged in, verifying...");
          try {
            await verifyLogin();
            
            const logingOneUrl = sessionStorage.getItem("logingsoneurl");
            if (logingOneUrl) {
              sessionStorage.removeItem("logingsoneurl");
              navigate(logingOneUrl.replace(window.location.origin, ""), { replace: true });
            } else {
              navigate("/services", { replace: true });
            }
          } catch (error) {
            console.error("Verification error:", error);
            Logger.e("Verification error:", error);
            handleToastOpen("error", "Authentication failed. Please try again.");
            setLoading(false);
            navigate("/login", { replace: true });
          }
        }

        return;
      }
    };

    handleOAuthCallback();
  }, [location.search, navigate, handleToastOpen]);

  // Verify existing login on component mount
  useEffect(() => {
    const verifyAndRedirect = async () => {
      const urlParams = new URLSearchParams(location.search);
      const hasOAuthParams = urlParams.has("msauth") || urlParams.has("error") || urlParams.has("login");

      // Skip verification if we have OAuth params (they'll be handled by the other effect)
      if (hasOAuthParams) {
        console.log("OAuth params present, skipping auto-verification");
        return;
      }

      console.log("Checking for existing login...");
      const userName = getUserName();
      
      if (userName !== null && userName !== "") {
        console.log("Found existing login, verifying...");
        setLoading(true);
        
        try {
          await verifyLogin();
          console.log("Login verified successfully");
          setLoading(false);

          // Check for redirect URL
          const logingOneUrl = sessionStorage.getItem("logingsoneurl");
          if (logingOneUrl) {
            console.log("Redirecting to stored URL:", logingOneUrl);
            sessionStorage.removeItem("logingsoneurl");
            navigate(logingOneUrl.replace(window.location.origin, ""), { replace: true });
          } else {
            console.log("Redirecting to /services");
            navigate("/services", { replace: true });
          }
        } catch (error) {
          console.error("Verification failed:", error);
          Logger.e("Verification error:", error);
          setLoading(false);
        }
      } else {
        console.log("No existing login found");
      }
    };

    verifyAndRedirect();
  }, []);

  const handleLogin = async () => {
    if (!email || email.trim() === "") {
      handleToastOpen("error", "Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      handleToastOpen("error", "Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      console.log("=== Starting Login ===");
      console.log("Email:", normalizedEmail);

      // Verify user exists
      await verificationUser(normalizedEmail);
      console.log("User verification successful");

      // Store email in session
      sessionStorage.setItem("email", normalizedEmail);

      // Get the base URL without query parameters
      const baseUrl = window.location.origin + window.location.pathname;

      // Construct Microsoft auth URL
      const redirectUrl = `${getBEUrl()}/users/auth/microsoft?feURL=${encodeURIComponent(
        baseUrl
      )}&email=${encodeURIComponent(normalizedEmail)}`;

      console.log("Redirecting to Microsoft auth...");
      console.log("Redirect URL:", redirectUrl);

      // Reset the processed flag before redirecting
      hasProcessedCallback.current = false;

      // Redirect to Microsoft authentication
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Login initiation error:", error);
      Logger.e("Login initiation error:", error);
      handleToastOpen("error", "Authentication failed. Please try again.");
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    handleLogin,
    loading
  };
};

export default LoginVM;